"use client";

import { Suspense, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@/components/providers/auth-provider";
import Link from "next/link";
import { usePathname, useRouter, useSearchParams } from "next/navigation";
import { useTranslations } from "next-intl";
import { toast } from "sonner";
import { ApiError } from "@/lib/api";
import { signInHref } from "@/lib/post-login-redirect";
import { getLanguageById, PLAYGROUND_LANGUAGES } from "../config/languages";
import {
  listLanguageVersions,
  type PlaygroundEngineId,
} from "../config/backend-language-contract.generated";
import {
  useForkSnippet,
  useIdePlan,
  useRunCode,
  useRuntimeCatalogue,
  useSaveSnippet,
  useSnippetById,
  useUpdateSnippet,
} from "../hooks";
import {
  loadCompilerOptions,
  loadLanguageVersion,
  saveCompilerOptions,
  saveLanguageVersion,
} from "../lib/language-version-storage";
import { RuntimeSettingsPanel } from "./RuntimeSettingsPanel";
import { clampFontSize } from "../lib/editor-settings";
import { useEditorSettingsState } from "../lib/use-editor-settings";
import { flushPlaygroundDraft, useScratchDraftPersistence } from "../lib/use-scratch-draft";
import {
  clearPlaygroundDraft,
  loadPlaygroundDraft,
  savePlaygroundDraft,
} from "../lib/playground-draft-storage";
import { playgroundErrorMessageKey } from "../lib/api-error-message";
import { RUN_LINK_VERSION_PARAM, runLinkVersion } from "../lib/run-link-version";
import { classifyNetworkFailure } from "../lib/network-error";
import { registerPlaygroundServiceWorker } from "../lib/playground-sw-client";
import { useConnectivity } from "../lib/use-connectivity";
import { useInstallPrompt } from "../lib/use-install-prompt";
import { PlaygroundConnectivityChip } from "./PlaygroundConnectivityChip";
import { FormatError, formatSupportFor } from "../lib/format-code";
import {
  decideSnippetHydration,
  resolveScratchSource,
  resolveStartupTarget,
  startupTargetKey,
} from "../lib/startup-source";
import {
  STDIN_MAX_LENGTH,
  type ExecutionResult,
  type PublicSharedSnippet,
  type SnippetEmbedSettings,
} from "../types";
import { PlaygroundEditor, type PlaygroundEditorHandle } from "./PlaygroundEditor";
import { SnippetHistoryPanel } from "./SnippetHistoryPanel";
import { PlaygroundLayout } from "./PlaygroundLayout";
import { PlaygroundEngineNotice } from "./PlaygroundEngineNotice";
import { PlaygroundSiteNoticeRow } from "./PlaygroundSiteNoticeRow";
import { PlaygroundPromoStrip } from "./PlaygroundPromoStrip";
import type { PublicPlaygroundPromo, PublicSiteNotice } from "@/lib/platform-public-settings";
import { PlaygroundOutput } from "./PlaygroundOutput";
import { PlaygroundStdinPanel } from "./PlaygroundStdinPanel";
import {
  playgroundDownloadFilename,
  prependProgsityIdeHeader,
  triggerTextFileDownload,
} from "../lib/source-download";
import { PlaygroundToolbar } from "./PlaygroundToolbar";
import { PlaygroundUpgradeModal } from "./PlaygroundUpgradeModal";
import { isFixEligible, kindFromResult } from "../lib/run-status";
import { ExplainCodeModal } from "./ExplainCodeModal";
import { FixErrorModal } from "./FixErrorModal";
import { SaveSnippetDialog, type SaveSnippetFormValues } from "./SaveSnippetDialog";

type LoadExampleRequest = {
  nonce: number;
  sourceCode: string;
  stdin?: string;
};

/**
 * Course lessons deep-link exercise starter code via `?code=<base64url(utf8)>`.
 * Self-contained (no snippet doc needed); anything invalid or oversized is
 * silently ignored so a mangled link still lands on a working playground.
 */
const MAX_URL_CODE_BYTES = 10_000;
/** The input beside `?code=`, base64url like it (`RV-13`, docs/24 §9.1). */
const URL_STDIN_PARAM = "stdin";

function decodeUrlCodeParam(raw: string | null): string | null {
  if (!raw || raw.length > MAX_URL_CODE_BYTES * 1.5) return null;
  try {
    const b64 = raw.replace(/-/g, "+").replace(/_/g, "/");
    const bin = window.atob(b64);
    if (bin.length > MAX_URL_CODE_BYTES) return null;
    const bytes = Uint8Array.from(bin, (ch) => ch.charCodeAt(0));
    const text = new TextDecoder("utf-8", { fatal: true }).decode(bytes);
    return text.trim() ? text : null;
  } catch {
    return null;
  }
}

type PlaygroundClientProps = {
  locale: string;
  initialLanguageId: string;
  /**
   * Which engine the API is serving (`EX-PUB04B`), read server-side.
   *
   * The version picker renders from the bundled contract so a failed catalogue
   * call cannot empty it, and the same contract holds both engines' rows. This
   * is the one bit that says which half to render, and it arrives with the first
   * paint rather than after one.
   */
  engine?: PlaygroundEngineId;
  /** From server `searchParams` — optional when using shared payload. */
  initialSnippetId?: string | null;
  /** Shared snippet route — loads read-only fork source; not a saved doc until user saves. */
  sharedPayload?: PublicSharedSnippet | null;
  /** Language SEO page: visible H1 lives in `LanguageInfoSection`; avoid duplicate sr-only heading. */
  suppressDocumentHeading?: boolean;
  /** "Try this" cards — apply example code when `nonce` changes. */
  loadExampleRequest?: LoadExampleRequest | null;
  /** SEO shell: footer is a draggable splitter above the article; hide inline footer. */
  omitFooter?: boolean;
  /** Language SEO pages render a guide article below the IDE — expose its settings toggle. */
  hasLanguageGuide?: boolean;
  /** `AD-SN01`: the admin-managed site notice, server-fetched by the page and composed into the toolbar's notice slot alongside `PlaygroundEngineNotice`. */
  siteNotice?: PublicSiteNotice;
  /** `GR-PGP01`: the admin-managed course promo strip, server-fetched by the page into the same notice slot. */
  playgroundPromo?: PublicPlaygroundPromo;
  /**
   * `SITE-SPD02`: the page renders per request, so the Suspense boundary
   * below has nothing to wait for. Around content this large it still made
   * React stream "Loading…" first and reveal the IDE a throttled frame or more
   * later (0.7 s after the first paint on the phone profile). A prerendered
   * page (the language pages) keeps the boundary: `useSearchParams` needs it
   * there.
   */
  renderedPerRequest?: boolean;
};

function PlaygroundClientInner({
  locale,
  initialLanguageId,
  engine = "runner",
  initialSnippetId,
  sharedPayload,
  suppressDocumentHeading = false,
  loadExampleRequest = null,
  omitFooter = false,
  hasLanguageGuide = false,
  siteNotice,
  playgroundPromo,
}: PlaygroundClientProps) {
  const t = useTranslations("playground");
  const { isAuthenticated, user } = useAuth();
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();

  const resolvedRouteLang = useMemo(() => {
    return getLanguageById(initialLanguageId) ?? getLanguageById("cpp")!;
  }, [initialLanguageId]);

  /**
   * The one startup order (`PG-UX05`, `lib/startup-source.ts`): shared payload,
   * then a saved snippet named by a valid `?snippet=`, then the route language
   * (whose buffer comes from `?code=`, the local draft or the default, in that
   * order). The key names the target this buffer was set up for; the effects
   * below apply a key once and never again, so a consumed query parameter or a
   * late response cannot re-run the startup over what the student typed since.
   */
  const snippetFromUrl = searchParams.get("snippet");
  const startupTarget = useMemo(
    () =>
      resolveStartupTarget({
        hasSharedPayload: sharedPayload != null,
        snippetId: initialSnippetId ?? snippetFromUrl,
        routeLanguageId: resolvedRouteLang.id,
      }),
    [initialSnippetId, resolvedRouteLang.id, sharedPayload, snippetFromUrl],
  );
  const startupKey = startupTargetKey(startupTarget);
  const effectiveSnippetId = startupTarget.kind === "snippet" ? startupTarget.snippetId : null;
  /** The shared payload is applied through initial state, so it starts as applied. */
  const appliedStartupKeyRef = useRef<string | null>(sharedPayload != null ? "shared" : null);
  /** The snippet id whose payload the buffer currently holds; a refetch of it is ignored. */
  const hydratedSnippetIdRef = useRef<string | null>(null);

  /**
   * `?code=` starter payload — decoded once per mount (ref survives the
   * route-language effect's re-runs without re-triggering them). Snippet and
   * shared routes take precedence.
   */
  const urlCodeRef = useRef<string | null | undefined>(undefined);
  /** `RV-13`: the input a `?code=` link carries beside it (`?stdin=`, same encoding). */
  const urlStdinRef = useRef<string | null>(null);
  if (urlCodeRef.current === undefined) {
    urlCodeRef.current =
      typeof window === "undefined" || sharedPayload != null || effectiveSnippetId != null
        ? null
        : decodeUrlCodeParam(searchParams.get("code"));
    urlStdinRef.current = urlCodeRef.current
      ? (decodeUrlCodeParam(searchParams.get(URL_STDIN_PARAM))?.slice(0, STDIN_MAX_LENGTH) ?? null)
      : null;
  }

  /**
   * `LN-WEB02`: the version a lesson's Run link names (`?version=c%2B%2B20`),
   * read once beside `?code=`. The version effect below applies it in place of
   * the stored preference, for the route language only, and never saves it as
   * the preference: the link describes one program, not the student's choice.
   * Not read into the initial state, which stays the contract default so the
   * server render and the first client render agree (see `languageVersion`).
   */
  const urlVersionRef = useRef<string | null | undefined>(undefined);
  if (urlVersionRef.current === undefined) {
    urlVersionRef.current =
      sharedPayload != null || effectiveSnippetId != null
        ? null
        : runLinkVersion(searchParams.get(RUN_LINK_VERSION_PARAM), resolvedRouteLang.id, engine);
  }

  const initialLang = useMemo(() => {
    if (sharedPayload) {
      return getLanguageById(sharedPayload.languageId) ?? resolvedRouteLang;
    }
    return resolvedRouteLang;
  }, [resolvedRouteLang, sharedPayload]);

  const [languageId, setLanguageId] = useState(initialLang.id);
  const [sourceCode, setSourceCode] = useState(
    sharedPayload?.sourceCode ?? initialLang.defaultCode,
  );
  const [baselineCode, setBaselineCode] = useState(
    sharedPayload?.sourceCode ?? initialLang.defaultCode,
  );
  const [stdin, setStdin] = useState(sharedPayload?.stdin ?? "");
  const [baselineStdin, setBaselineStdin] = useState(sharedPayload?.stdin ?? "");
  const [result, setResult] = useState<ExecutionResult | null>(null);
  const [runError, setRunError] = useState<string | null>(null);
  const editorHandleRef = useRef<PlaygroundEditorHandle>(null);
  const [isFormatting, setIsFormatting] = useState(false);

  const [savedSnippetId, setSavedSnippetId] = useState<string | null>(null);
  const [shareSlug, setShareSlug] = useState<string | null>(null);
  const [isPasswordProtected, setIsPasswordProtected] = useState(false);
  /** The owner's embed opt-in, so the share panel opens on the truth (`PG-EMBED01`). */
  const [snippetEmbed, setSnippetEmbed] = useState<SnippetEmbedSettings | undefined>(undefined);
  const [snippetTitle, setSnippetTitle] = useState(
    sharedPayload?.title ?? t("defaultSnippetTitle"),
  );
  const [snippetDescription, setSnippetDescription] = useState(
    sharedPayload?.description ?? "",
  );

  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [explainModalOpen, setExplainModalOpen] = useState(false);
  const [fixModalOpen, setFixModalOpen] = useState(false);
  const [explainUpgradeOpen, setExplainUpgradeOpen] = useState(false);
  const allowedSnippetHydrationRef = useRef<string | null>(null);

  const [editorSettings, setEditorSettings] = useEditorSettingsState();

  /**
   * Runtime version + compiler flags (`PG-IDE01`).
   *
   * Seeded from the contract — never from `localStorage`, which does not exist
   * on the server and would hydrate as a mismatch. The stored preference is
   * reconciled in an effect after mount, which is also where a pin the contract
   * no longer declares becomes a visible fallback rather than a silent reset.
   */
  const [languageVersion, setLanguageVersion] = useState(
    () => sharedPayload?.version?.id ?? initialLang.defaultVersionId[engine],
  );
  const [compilerOptions, setCompilerOptions] = useState<string[]>([]);
  const { data: runtimeCatalogue } = useRuntimeCatalogue();

  /** One notice, wherever a version had to be substituted. */
  const noticeVersionFallback = useCallback(
    (requested: string, resolvedLabel: string) => {
      toast.warning(t("runtimeFallbackTitle"), {
        id: `playground-version-fallback-${requested}`,
        description: t("runtimeFallbackDescription", {
          requested,
          version: resolvedLabel,
        }),
      });
    },
    [t],
  );

  /**
   * The other notice, and it is deliberately not the one above.
   *
   * A pin that vanished is an apology: something the student chose is gone. A
   * pin that was *superseded* is an announcement: the platform moved Python 3.8
   * to 3.11 and their code ran on the newer one. Same mechanism, different fact,
   * so different words and a different tone — informational, not a warning,
   * because nothing went wrong. The 2026-08-18 decision (point 5) requires this
   * be said out loud rather than absorbed silently.
   */
  const noticeVersionSuperseded = useCallback(
    (requested: string, resolvedLabel: string) => {
      toast.info(t("runtimeSupersededTitle"), {
        id: `playground-version-superseded-${requested}`,
        description: t("runtimeSupersededDescription", {
          requested,
          version: resolvedLabel,
        }),
      });
    },
    [t],
  );

  const saveMutation = useSaveSnippet();
  const updateMutation = useUpdateSnippet();
  const forkMutation = useForkSnippet();

  const snippetIdForQuery = effectiveSnippetId ?? "";
  const shouldLoadSnippet = startupTarget.kind === "snippet";

  const {
    data: loadedSnippet,
    isLoading: snippetLoading,
    error: snippetLoadError,
  } = useSnippetById(snippetIdForQuery, shouldLoadSnippet);

  /**
   * A `?snippet=` that could not be fetched because the request never got an
   * answer (`PG-PWA01`): said once, in the offline or unreachable wording,
   * rather than leaving a default template with no explanation. An answered
   * error (404, 403) keeps the existing behaviour.
   */
  useEffect(() => {
    if (!snippetLoadError) return;
    const kind = classifyNetworkFailure(snippetLoadError);
    if (!kind) return;
    toast.warning(t("snippetOfflineLoad"), { id: "playground-snippet-offline" });
  }, [snippetLoadError, t]);

  /** SEO "Try this" — load example into editor and drop saved-snippet association. */
  useEffect(() => {
    if (!loadExampleRequest) return;
    const { sourceCode: nextCode, stdin: nextStdin } = loadExampleRequest;
    setSourceCode(nextCode);
    setStdin(nextStdin ?? "");
    setBaselineCode(nextCode);
    setBaselineStdin(nextStdin ?? "");
    setSavedSnippetId(null);
    setShareSlug(null);
    setIsPasswordProtected(false);
    setSnippetTitle(t("defaultSnippetTitle"));
    setSnippetDescription("");
    setResult(null);
    setRunError(null);
    // Stripping `?snippet=` moves the startup target to the route language.
    // The example is the buffer now, so that target is recorded as applied
    // before the URL changes; otherwise the language startup would run and
    // replace the example with the draft or the default.
    hydratedSnippetIdRef.current = null;
    appliedStartupKeyRef.current = startupTargetKey({
      kind: "language",
      languageId: resolvedRouteLang.id,
    });
    const params = new URLSearchParams(searchParams.toString());
    params.delete("snippet");
    const qs = params.toString();
    router.replace(qs ? `${pathname}?${qs}` : pathname);
  }, [
    loadExampleRequest?.nonce,
    loadExampleRequest?.sourceCode,
    loadExampleRequest?.stdin,
    pathname,
    resolvedRouteLang.id,
    router,
    t,
  ]);

  /**
   * Hydrate from a loaded snippet (GET by id), once per snippet id.
   *
   * `decideSnippetHydration` is the rule: a payload for the id already in the
   * buffer is a refetch (after a save, on window focus) and is ignored, so a
   * response that lands after the student kept typing never overwrites the
   * typing; a payload arriving over unsaved edits asks first, unless the
   * quick-access picker already approved opening this exact id.
   */
  useEffect(() => {
    if (!loadedSnippet || sharedPayload) return;
    const decision = decideSnippetHydration({
      loadedSnippetId: loadedSnippet._id,
      hydratedSnippetId: hydratedSnippetIdRef.current,
      hasUnsavedChanges: sourceCode !== baselineCode || stdin !== baselineStdin,
      approvedSnippetId: allowedSnippetHydrationRef.current,
    });
    if (decision === "skip") return;

    if (decision === "confirm" && !window.confirm(t("unsavedSnippetOpen"))) {
      // Keep what the student typed. The URL goes back to the snippet the
      // buffer came from, or to the plain route, and that target is recorded
      // as applied so the language startup does not run over the buffer.
      const params = new URLSearchParams(searchParams.toString());
      if (savedSnippetId) {
        params.set("snippet", savedSnippetId);
        appliedStartupKeyRef.current = startupTargetKey({
          kind: "snippet",
          snippetId: savedSnippetId,
        });
      } else {
        params.delete("snippet");
        appliedStartupKeyRef.current = startupTargetKey({
          kind: "language",
          languageId: resolvedRouteLang.id,
        });
      }
      const qs = params.toString();
      router.replace(qs ? `${pathname}?${qs}` : pathname);
      return;
    }

    allowedSnippetHydrationRef.current = null;
    hydratedSnippetIdRef.current = loadedSnippet._id;
    appliedStartupKeyRef.current = startupTargetKey({
      kind: "snippet",
      snippetId: loadedSnippet._id,
    });
    setLanguageId(loadedSnippet.languageId);
    // The server resolved the stored pin and told us whether it had to
    // substitute one. A snippet saved against a version that has since been
    // retired opens on a supported one and *says so* — the fallback is visible
    // precisely because the payload carries what it replaced.
    const snippetVersion = loadedSnippet.version;
    setLanguageVersion(
      (current) =>
        snippetVersion?.id ??
        getLanguageById(loadedSnippet.languageId)?.defaultVersionId[engine] ??
        current,
    );
    setCompilerOptions(loadCompilerOptions(loadedSnippet.languageId, engine));
    if (snippetVersion?.fellBackFrom) {
      noticeVersionFallback(snippetVersion.fellBackFrom, snippetVersion.label);
    } else if (snippetVersion?.supersededFrom) {
      // "Saved on Python 3.8, opened on Python 3.11" — the snippet keeps the pin
      // its author chose; only the reading of it moves with the engine.
      noticeVersionSuperseded(snippetVersion.supersededFrom, snippetVersion.label);
    }
    setSourceCode(loadedSnippet.sourceCode);
    setBaselineCode(loadedSnippet.sourceCode);
    setStdin(loadedSnippet.stdin ?? "");
    setBaselineStdin(loadedSnippet.stdin ?? "");
    setSavedSnippetId(loadedSnippet._id);
    setShareSlug(loadedSnippet.shareSlug);
    setIsPasswordProtected(loadedSnippet.isPasswordProtected);
    setSnippetEmbed(loadedSnippet.embed);
    setSnippetTitle(loadedSnippet.title);
    setSnippetDescription(loadedSnippet.description ?? "");
    setResult(null);
    setRunError(null);
  }, [engine, loadedSnippet, noticeVersionFallback, noticeVersionSuperseded, sharedPayload]);

  /**
   * The route-language startup (direct `/playground`, the SEO pages, back and
   * forward between them): steps 2 to 4 of the order, applied once per key.
   * A key already applied is a consumed `?code=` or `?snippet=`, or a declined
   * hydration; nothing here may run over the buffer in those cases.
   */
  useEffect(() => {
    if (startupTarget.kind !== "language") return;
    if (appliedStartupKeyRef.current === startupKey) return;
    appliedStartupKeyRef.current = startupKey;
    hydratedSnippetIdRef.current = null;
    const lang = resolvedRouteLang;

    // Lesson-exercise starter from `?code=` wins over the scratch draft; the
    // param is consumed (and stripped from the URL) so reloads fall back to
    // normal draft behavior.
    const urlCode = urlCodeRef.current ?? null;
    const urlStdin = urlStdinRef.current;
    urlCodeRef.current = null;
    urlStdinRef.current = null;
    const source = resolveScratchSource({
      urlCode,
      urlStdin,
      draft: loadPlaygroundDraft(lang.id),
      defaultCode: lang.defaultCode,
    });

    setLanguageId(lang.id);
    setSourceCode(source.sourceCode);
    /** A draft is already persisted; it is the baseline so switching away does not warn. */
    setBaselineCode(source.sourceCode);
    setStdin(source.stdin);
    setBaselineStdin(source.stdin);
    setResult(null);
    setRunError(null);
    setSavedSnippetId(null);
    setShareSlug(null);
    setIsPasswordProtected(false);
    setSnippetTitle(t("defaultSnippetTitle"));
    setSnippetDescription("");

    if (source.source === "url-code") {
      const params = new URLSearchParams(searchParams.toString());
      params.delete("code");
      params.delete(RUN_LINK_VERSION_PARAM);
      params.delete(URL_STDIN_PARAM);
      const qs = params.toString();
      router.replace(qs ? `${pathname}?${qs}` : pathname);
      return;
    }
    if (source.source === "draft") {
      toast(t("draftRestored"), {
        id: "playground-draft-restored",
        action: {
          label: t("draftDiscard"),
          onClick: () => {
            clearPlaygroundDraft(lang.id);
            setSourceCode(lang.defaultCode);
            setBaselineCode(lang.defaultCode);
            setStdin("");
            setBaselineStdin("");
          },
        },
      });
    }
  }, [resolvedRouteLang, startupKey, startupTarget.kind, t]);

  const currentLang = useMemo(
    () => getLanguageById(languageId) ?? getLanguageById("cpp")!,
    [languageId],
  );

  /**
   * Per-language runtime preferences follow the language — except when a snippet
   * owns the pin, in which case the snippet's hydration sets it and this must
   * not overwrite it a tick later.
   */
  useEffect(() => {
    if (shouldLoadSnippet || sharedPayload) return;
    // `LN-WEB02`: a lesson link's version wins while the route language is
    // open; switching language drops it, so coming back reads the preference.
    const linked = urlVersionRef.current;
    if (linked && languageId === resolvedRouteLang.id) {
      setLanguageVersion(linked);
      setCompilerOptions(loadCompilerOptions(languageId, engine));
      return;
    }
    urlVersionRef.current = null;
    const stored = loadLanguageVersion(languageId, engine);
    const resolved = stored?.versionId ?? currentLang.defaultVersionId[engine];
    setLanguageVersion(resolved);
    setCompilerOptions(loadCompilerOptions(languageId, engine));
    const resolvedLabel =
      listLanguageVersions(languageId, engine).find((version) => version.id === resolved)?.label ??
      resolved;
    if (stored?.fellBackFrom) {
      noticeVersionFallback(stored.fellBackFrom, resolvedLabel);
    } else if (stored?.supersededFrom) {
      // A preference set before the cutover. Same mechanism as the run response,
      // same words, so the two never disagree about what happened.
      noticeVersionSuperseded(stored.supersededFrom, resolvedLabel);
    }
  }, [
    currentLang.defaultVersionId,
    engine,
    languageId,
    noticeVersionFallback,
    noticeVersionSuperseded,
    resolvedRouteLang.id,
    sharedPayload,
    shouldLoadSnippet,
  ]);

  const handleLanguageVersionChange = useCallback(
    (versionId: string) => {
      setLanguageVersion(versionId);
      saveLanguageVersion(languageId, versionId, engine);
    },
    [engine, languageId],
  );

  const handleCompilerOptionsChange = useCallback(
    (next: string[]) => {
      setCompilerOptions(next);
      saveCompilerOptions(languageId, next, engine);
    },
    [engine, languageId],
  );

  /**
   * The offline shell (`PG-PWA01`, ADR-0052). The worker is registered from
   * the shell routes only, never from a share page (which carries somebody
   * else's source and is never cached); `online` drives the local-only state
   * of every server-backed action, and a shell served from the worker's cache
   * offers a reload once the network is back.
   */
  const { online, shellSource } = useConnectivity();
  const { canInstall, install } = useInstallPrompt();
  useEffect(() => {
    if (sharedPayload != null) return;
    void registerPlaygroundServiceWorker(locale);
  }, [locale, sharedPayload]);

  const scratchDraftEnabled = !sharedPayload && !shouldLoadSnippet && !savedSnippetId;
  const onDraftPersistFailure = useCallback(() => {
    toast.warning(t("draftNotStored"), { id: "playground-draft-not-stored", duration: 10_000 });
  }, [t]);
  useScratchDraftPersistence({
    enabled: scratchDraftEnabled,
    languageId,
    sourceCode,
    stdin,
    onPersistFailure: onDraftPersistFailure,
  });

  const handleReloadShell = useCallback(() => {
    // The draft goes to disk ahead of the debounce so the reload cannot lose
    // the last keystrokes; a saved-snippet buffer is guarded by beforeunload.
    if (scratchDraftEnabled) flushPlaygroundDraft({ languageId, sourceCode, stdin });
    window.location.reload();
  }, [languageId, scratchDraftEnabled, sourceCode, stdin]);

  /** One wording for a request that never got an answer, whatever it was for. */
  const networkFailureMessage = useCallback(
    (error: unknown): string | null => {
      const kind = classifyNetworkFailure(error);
      if (kind === "offline") return t("errorOffline");
      if (kind === "unreachable") return t("errorUnreachable");
      return null;
    },
    [t],
  );

  const isDirty = sourceCode !== baselineCode || stdin !== baselineStdin;

  /** Saved-snippet / shared edits aren't drafted — warn before the tab closes on them. */
  useEffect(() => {
    if (!isDirty || (savedSnippetId == null && sharedPayload == null)) return;
    const onBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = "";
    };
    window.addEventListener("beforeunload", onBeforeUnload);
    return () => window.removeEventListener("beforeunload", onBeforeUnload);
  }, [isDirty, savedSnippetId, sharedPayload]);

  const runMutation = useRunCode();
  const { data: idePlanData } = useIdePlan(isAuthenticated);

  const handleLanguageChange = useCallback(
    (nextId: string) => {
      if (nextId === languageId) return;
      const nextLang = getLanguageById(nextId);
      if (!nextLang) return;

      if (savedSnippetId != null || sharedPayload != null) {
        const dirty =
          sourceCode !== baselineCode || stdin !== baselineStdin;
        if (dirty && !window.confirm(t("unsavedLanguageChange"))) return;
      } else {
        /** Scratch work is drafted, not lost — flush ahead of the debounce before navigating. */
        const lang = getLanguageById(languageId);
        const pristine = lang != null && sourceCode === lang.defaultCode && stdin === "";
        if (!pristine) savePlaygroundDraft(languageId, { sourceCode, stdin });
      }

      router.replace(`/${locale}/playground/${nextLang.seoSlug}`);
    },
    [
      baselineCode,
      baselineStdin,
      languageId,
      locale,
      router,
      savedSnippetId,
      sharedPayload,
      sourceCode,
      stdin,
      t,
    ],
  );

  const handleRun = useCallback(async () => {
    if (!sourceCode.trim()) {
      toast.error(t("runEmpty"));
      return;
    }

    setRunError(null);

    try {
      const data = await runMutation.mutateAsync({
        languageId,
        sourceCode,
        stdin,
        // Both server-validated: a version id from the contract, and exact
        // tokens from the language's own allowlist. Neither is free text.
        compilerOptions: compilerOptions.join(" "),
        languageVersion,
      });
      setResult(data);
      if (data.version?.fellBackFrom) {
        noticeVersionFallback(data.version.fellBackFrom, data.version.label);
      } else if (data.version?.supersededFrom) {
        noticeVersionSuperseded(data.version.supersededFrom, data.version.label);
      }
    } catch (e) {
      const key = playgroundErrorMessageKey(e);
      const msg = key ? t(key) : e instanceof ApiError ? e.message : t("runFailed");
      setRunError(msg);
    }
  }, [
    compilerOptions,
    languageId,
    languageVersion,
    noticeVersionFallback,
    noticeVersionSuperseded,
    runMutation,
    sourceCode,
    stdin,
    t,
  ]);

  const clearOutput = useCallback(() => {
    setResult(null);
    setRunError(null);
  }, []);

  const handleExplainClick = useCallback(() => {
    if (!isAuthenticated) {
      router.push(signInHref(locale, pathname || `/${locale}/playground`));
      return;
    }
    const ide = idePlanData?.plan;
    if (ide !== "pro" && ide !== "internal") {
      setExplainUpgradeOpen(true);
      return;
    }
    setExplainModalOpen(true);
  }, [idePlanData?.plan, isAuthenticated, locale, pathname, router]);

  const handleDownloadFile = useCallback(() => {
    const withHeader = prependProgsityIdeHeader(sourceCode, currentLang);
    triggerTextFileDownload(withHeader, playgroundDownloadFilename(currentLang));
  }, [currentLang, sourceCode]);

  /**
   * Format Code (`PG-IDE02`). One handler for the toolbar, the overflow menu
   * and Shift+Alt+F, so every entry point gives the same localized feedback:
   * formatted, already formatted, not available for this language, or one of
   * the three failure classes the formatter distinguishes.
   */
  const formatSupport = formatSupportFor(currentLang.monacoLanguage);
  const handleFormat = useCallback(async () => {
    if (isFormatting) return;
    if (formatSupport === "unsupported") {
      toast.message(t("formatUnsupported", { language: currentLang.name }));
      return;
    }
    const handle = editorHandleRef.current;
    if (!handle) return;
    setIsFormatting(true);
    try {
      const outcome = await handle.format();
      if (outcome === "formatted") toast.success(t("formatDone"));
      else if (outcome === "unchanged") toast.message(t("formatUnchanged"));
      else toast.message(t("formatUnsupported", { language: currentLang.name }));
    } catch (e) {
      const reason = e instanceof FormatError ? e.reason : "unknown";
      toast.error(
        reason === "syntax"
          ? t("formatFailedSyntax")
          : reason === "load"
            ? t("formatFailedLoad")
            : t("formatFailed"),
      );
    } finally {
      setIsFormatting(false);
    }
  }, [currentLang.name, formatSupport, isFormatting, t]);

  const canRun = sourceCode.trim().length > 0 && !runMutation.isPending && online;
  const runDisabledReason = !online ? t("offlineRunDisabled") : undefined;
  const canDownloadSource = sourceCode.trim().length > 0;

  const syncSnippetQuery = useCallback(
    (id: string | null) => {
      const params = new URLSearchParams(searchParams.toString());
      if (id) params.set("snippet", id);
      else params.delete("snippet");
      const qs = params.toString();
      router.replace(qs ? `${pathname}?${qs}` : pathname);
    },
    [pathname, router, searchParams],
  );

  /**
   * `sourceOverride` exists for the version restore (`PG-IDE05`): the editor
   * edit fires `onChange`, but `sourceCode` state has not re-rendered by the
   * time the save runs, so the restored text is passed in rather than read back
   * from a value that is one render behind.
   */
  const handleSaveInPlace = useCallback(async (sourceOverride?: string): Promise<boolean> => {
    if (!savedSnippetId) return false;
    const sourceToSave = sourceOverride ?? sourceCode;
    try {
      const updated = await updateMutation.mutateAsync({
        id: savedSnippetId,
        input: {
          title: snippetTitle,
          description: snippetDescription || null,
          languageId,
          languageVersion,
          sourceCode: sourceToSave,
          stdin: stdin || null,
        },
      });
      setShareSlug(updated.shareSlug);
      setIsPasswordProtected(updated.isPasswordProtected);
      setSnippetEmbed(updated.embed);
      setBaselineCode(sourceToSave);
      setBaselineStdin(stdin);
      toast.success(t("saveUpdated"));
      return true;
    } catch (e) {
      if (e instanceof ApiError && e.status === 403 && e.planLimit?.upgradeRequired) {
        toast.error(t("saveLimitTitle"), { description: t("saveLimitDescription") });
        return false;
      }
      const msg = networkFailureMessage(e) ?? (e instanceof ApiError ? e.message : t("saveFailed"));
      toast.error(msg);
      return false;
    }
  }, [
    savedSnippetId,
    updateMutation,
    snippetTitle,
    snippetDescription,
    languageId,
    languageVersion,
    networkFailureMessage,
    sourceCode,
    stdin,
    t,
  ]);

  const handleCreateSubmit = useCallback(
    async (values: SaveSnippetFormValues) => {
      if (!isAuthenticated) return;
      try {
        const created = await saveMutation.mutateAsync({
          title: values.title,
          description: values.description ?? null,
          languageId,
          languageVersion,
          sourceCode,
          stdin: stdin || null,
        });
        setSavedSnippetId(created._id);
        setShareSlug(created.shareSlug);
        setIsPasswordProtected(created.isPasswordProtected);
        setSnippetEmbed(created.embed);
        setSnippetTitle(created.title);
        setSnippetDescription(created.description ?? "");
        setBaselineCode(sourceCode);
        setBaselineStdin(stdin);
        clearPlaygroundDraft(languageId);
        // The buffer already is this snippet. The `?snippet=` the URL is about
        // to gain triggers a fetch of it, and that fetch must not re-hydrate
        // over anything typed in the meantime.
        hydratedSnippetIdRef.current = created._id;
        appliedStartupKeyRef.current = startupTargetKey({
          kind: "snippet",
          snippetId: created._id,
        });
        syncSnippetQuery(created._id);
        toast.success(t("saveCreated"));
        setSaveDialogOpen(false);
      } catch (e) {
        if (e instanceof ApiError && e.status === 403 && e.planLimit?.upgradeRequired) {
          toast.error(t("saveLimitTitle"), { description: t("saveLimitDescription") });
          return;
        }
        const msg = networkFailureMessage(e) ?? (e instanceof ApiError ? e.message : t("saveFailed"));
        toast.error(msg);
      }
    },
    [
      isAuthenticated,
      saveMutation,
      languageId,
      languageVersion,
      networkFailureMessage,
      sourceCode,
      stdin,
      syncSnippetQuery,
      t,
    ],
  );

  /**
   * Restore a stored version (`PG-IDE05`).
   *
   * Three deliberate steps. A dirty buffer is confirmed first, the same way a
   * snippet load and a language switch already are, so a restore can never
   * silently take unsaved work. The text goes in through the editor handle as
   * one undo step, so Ctrl+Z brings back exactly what was there. Then it is an
   * ordinary owner save, through the same PATCH and the same Zod validation as
   * any other save, which is also what puts the replaced state into the history
   * as its newest version: nothing this button does is unrecoverable.
   */
  /**
   * Fix My Error (`PG-AI02`, ADR-0051).
   *
   * Signed in is the only gate. It is not an IDE Pro feature: this is the
   * surface a student reaches when they cannot proceed, and one AI credit from
   * the allowance their plan already funds is the bound.
   */
  const handleFixErrorClick = useCallback(() => {
    if (!isAuthenticated) {
      router.push(signInHref(locale, pathname || `/${locale}/playground`));
      return;
    }
    setFixModalOpen(true);
  }, [isAuthenticated, locale, pathname, router]);

  /**
   * The request the modal sends, frozen from the run that failed.
   *
   * `null` when the current result is not eligible, which is also what hides
   * the call to action. Built here rather than in the modal so the modal can
   * never quietly re-read a result that has since been cleared.
   */
  const fixErrorRequest = useMemo(() => {
    if (!result || !isFixEligible(result)) return null;
    const kind = kindFromResult(result);
    if (kind !== "compilation_error" && kind !== "runtime_error") return null;
    return {
      languageId,
      sourceCode,
      outcome: kind,
      locale: locale === "bn" ? ("bn" as const) : ("en" as const),
      compileOutput: result.compileOutput,
      stderr: result.stderr,
      stdout: result.stdout,
    };
  }, [result, languageId, sourceCode, locale]);

  /**
   * Applies a suggested fix as one undoable editor edit (`PG-AI02`).
   *
   * The same three steps `handleRestoreVersion` uses, minus the save: a fix is
   * a suggestion the student is trying, not a decision they have committed to,
   * so it goes into the buffer and no further. Ctrl+Z brings back exactly what
   * was there.
   */
  const handleApplyFix = useCallback(
    (patchedSource: string): boolean => {
      const applied = editorHandleRef.current?.restore(patchedSource) ?? false;
      if (!applied) return false;
      setSourceCode(patchedSource);
      return true;
    },
    [],
  );

  const handleRestoreVersion = useCallback(
    (source: string) => {
      if (!savedSnippetId) return;
      if (isDirty && !window.confirm(t("historyRestoreConfirm"))) return;
      const applied = editorHandleRef.current?.restore(source) ?? false;
      if (!applied) {
        toast.info(t("historyRestoreUnchanged"));
        return;
      }
      setSourceCode(source);
      // Only on a save that actually landed: `handleSaveInPlace` reports its
      // own failure, and two toasts saying opposite things is worse than none.
      void handleSaveInPlace(source).then((saved) => {
        if (saved) toast.success(t("historyRestored"));
      });
    },
    [handleSaveInPlace, isDirty, savedSnippetId, t],
  );

  const handleSaveClick = useCallback(() => {
    if (!isAuthenticated) {
      const nextPath = `${pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
      router.push(signInHref(locale, nextPath));
      return;
    }
    if (savedSnippetId) {
      void handleSaveInPlace();
      return;
    }
    setSaveDialogOpen(true);
  }, [
    handleSaveInPlace,
    isAuthenticated,
    locale,
    pathname,
    router,
    savedSnippetId,
    searchParams,
  ]);

  const handleFork = useCallback(async () => {
    if (!sharedPayload) return;
    if (!isAuthenticated) {
      router.push(signInHref(locale, pathname || `/${locale}/playground`));
      return;
    }
    try {
      const forked = await forkMutation.mutateAsync(sharedPayload._id);
      const lang = getLanguageById(forked.languageId);
      router.push(
        `/${locale}/playground/${lang?.seoSlug ?? "cpp"}?snippet=${encodeURIComponent(forked._id)}`,
      );
      toast.success(t("forkSuccess"));
    } catch (e) {
      const msg = networkFailureMessage(e) ?? (e instanceof ApiError ? e.message : t("forkFailed"));
      toast.error(msg);
    }
  }, [forkMutation, isAuthenticated, locale, networkFailureMessage, pathname, router, sharedPayload, t]);

  const handleSnippetSelect = useCallback(
    (snippetId: string, snippetLanguageId: string) => {
      if (snippetId === savedSnippetId) return;
      if (
        isDirty &&
        (savedSnippetId != null || sharedPayload != null) &&
        !window.confirm(t("unsavedSnippetOpen"))
      ) {
        return;
      }

      if (savedSnippetId == null && sharedPayload == null) {
        const language = getLanguageById(languageId);
        const pristine =
          language != null && sourceCode === language.defaultCode && stdin === "";
        if (!pristine) {
          savePlaygroundDraft(languageId, { sourceCode, stdin });
        }
      }

      allowedSnippetHydrationRef.current = snippetId;
      const targetLanguage =
        getLanguageById(snippetLanguageId) ?? getLanguageById("cpp")!;
      router.push(
        `/${locale}/playground/${targetLanguage.seoSlug}?snippet=${encodeURIComponent(snippetId)}`,
      );
    },
    [
      isDirty,
      languageId,
      locale,
      router,
      savedSnippetId,
      sharedPayload,
      sourceCode,
      stdin,
      t,
    ],
  );

  const heading = useMemo(() => {
    const segments = pathname?.split("/").filter(Boolean) ?? [];
    const last = segments[segments.length - 1];
    const isMainPlayground = last === "playground";
    if (isMainPlayground) return t("seo.mainH1");
    return t("seo.languageH1", { language: currentLang.name });
  }, [currentLang.name, pathname, t]);

  const savePending = saveMutation.isPending || updateMutation.isPending;
  const saveDisabled = savePending || snippetLoading || !online;
  const saveTooltip = !online
    ? t("offlineSaveDisabled")
    : !isAuthenticated
      ? t("signInToSaveTooltip")
      : undefined;

  /**
   * Ctrl/Cmd+S saves the snippet instead of opening the browser dialog (editor
   * focus included), and never silently does nothing (`PG-UX04`): an owner
   * saves or updates; a signed-in reader of a shared snippet forks it, which
   * is what "save" means for a copy; the author of a shared snippet is pointed
   * at the editable original; a signed-out person gets a sign-in path as a
   * toast action, so their buffer stays exactly where it is until they choose.
   */
  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (!(e.ctrlKey || e.metaKey) || e.altKey || e.shiftKey) return;
      if (e.key.toLowerCase() !== "s") return;
      e.preventDefault();
      const currentPath = `${pathname}${searchParams.toString() ? `?${searchParams.toString()}` : ""}`;
      const signInAction = {
        label: t("signInLink"),
        onClick: () => router.push(signInHref(locale, currentPath)),
      };
      if (sharedPayload != null) {
        if (!isAuthenticated) {
          toast(t("signInToSaveCopy"), { id: "playground-save-signin", action: signInAction });
          return;
        }
        const isOwner =
          !!user?.username &&
          !!sharedPayload.author.username &&
          user.username === sharedPayload.author.username;
        if (isOwner) {
          const lang = getLanguageById(sharedPayload.languageId);
          toast(t("sharedOwnSnippet"), {
            id: "playground-save-own-share",
            action: {
              label: t("sharedOpenToEdit"),
              onClick: () =>
                router.push(
                  `/${locale}/playground/${lang?.seoSlug ?? "cpp"}?snippet=${encodeURIComponent(sharedPayload._id)}`,
                ),
            },
          });
          return;
        }
        if (!forkMutation.isPending) void handleFork();
        return;
      }
      if (!isAuthenticated) {
        toast(t("signInToSaveTooltip"), { id: "playground-save-signin", action: signInAction });
        return;
      }
      if (saveDisabled) return;
      handleSaveClick();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [
    forkMutation.isPending,
    handleFork,
    handleSaveClick,
    isAuthenticated,
    locale,
    pathname,
    router,
    saveDisabled,
    searchParams,
    sharedPayload,
    t,
    user?.username,
  ]);

  const shareBanner =
    sharedPayload != null ? (
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-sm text-white/85">
          <span className="text-white/45">{t("sharedBy")} </span>
          {sharedPayload.author.username ? (
            <Link
              href={`/${locale}/u/${encodeURIComponent(sharedPayload.author.username)}`}
              className="font-medium text-white underline-offset-2 hover:underline"
            >
              {sharedPayload.author.displayName}
            </Link>
          ) : (
            <span className="font-medium text-white">{sharedPayload.author.displayName}</span>
          )}
        </p>
        <button
          type="button"
          onClick={() => void handleFork()}
          disabled={forkMutation.isPending}
          className="inline-flex items-center gap-2 rounded-lg border border-[#E94D1D]/50 bg-[#E94D1D]/15 px-3 py-1.5 text-sm font-semibold text-[#FFAB8F] transition hover:bg-[#E94D1D]/25 disabled:opacity-50"
        >
          {forkMutation.isPending ? t("forking") : t("forkToEdit")}
        </button>
      </div>
    ) : null;

  return (
    <div className={omitFooter ? "flex h-full min-h-0 flex-1 flex-col" : "flex min-h-0 flex-1 flex-col"}>
      {!suppressDocumentHeading ? <h1 className="sr-only">{heading}</h1> : null}
      <PlaygroundLayout
        notice={
          <>
            <PlaygroundEngineNotice />
            <PlaygroundSiteNoticeRow notice={siteNotice} locale={locale === "bn" ? "bn" : "en"} />
            <PlaygroundPromoStrip promo={playgroundPromo} locale={locale === "bn" ? "bn" : "en"} />
          </>
        }
        shareBanner={shareBanner}
        toolbar={
          <PlaygroundToolbar
            locale={locale}
            isAuthenticated={isAuthenticated}
            languages={PLAYGROUND_LANGUAGES}
            currentLanguageId={languageId}
            onLanguageChange={handleLanguageChange}
            onRun={() => void handleRun()}
            isRunning={runMutation.isPending}
            canRun={canRun}
            onFormat={() => void handleFormat()}
            isFormatting={isFormatting}
            formatSupport={formatSupport}
            onDownloadFile={isAuthenticated ? handleDownloadFile : undefined}
            canDownloadSource={canDownloadSource}
            editorSettings={editorSettings}
            onEditorSettingsChange={setEditorSettings}
            onSaveClick={handleSaveClick}
            onSnippetSelect={handleSnippetSelect}
            savePending={savePending}
            saveDisabled={saveDisabled}
            saveDisabledTooltip={saveTooltip}
            savedSnippetId={savedSnippetId}
            shareSlug={shareSlug}
            isPasswordProtected={isPasswordProtected}
            {...(snippetEmbed ? { snippetEmbed } : {})}
            showLanguageGuideToggle={hasLanguageGuide}
            status={
              <PlaygroundConnectivityChip
                online={online}
                shellSource={shellSource}
                onReload={handleReloadShell}
              />
            }
            offline={!online}
            runDisabledReason={runDisabledReason}
            onInstall={canInstall ? () => void install() : undefined}
            runtimePanel={
              <RuntimeSettingsPanel
                languageId={languageId}
                languageVersion={languageVersion}
                onLanguageVersionChange={handleLanguageVersionChange}
                engine={engine}
                allowedCompilerOptions={currentLang.allowedCompilerOptions[engine]}
                selectedCompilerOptions={compilerOptions}
                onCompilerOptionsChange={handleCompilerOptionsChange}
                catalogue={runtimeCatalogue}
              />
            }
            historyPanel={
              savedSnippetId ? (
                <SnippetHistoryPanel
                  snippetId={savedSnippetId}
                  currentSource={sourceCode}
                  canRestore={
                    idePlanData?.plan === "pro" || idePlanData?.plan === "internal"
                  }
                  onRestore={handleRestoreVersion}
                  onUpgradeClick={() => setExplainUpgradeOpen(true)}
                />
              ) : null
            }
          />
        }
        editor={
          <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden px-2 pb-2 pt-2 lg:px-3 lg:pb-3 lg:pt-3">
            <PlaygroundEditor
              key={languageId}
              ref={editorHandleRef}
              fillParent={omitFooter}
              language={currentLang.monacoLanguage}
              value={sourceCode}
              onChange={setSourceCode}
              settings={editorSettings}
              onFontSizeChange={(next) =>
                setEditorSettings((s) => ({ ...s, fontSize: clampFontSize(next) }))
              }
              onRun={() => void handleRun()}
              onFormatShortcut={() => void handleFormat()}
              aria-label={t("editorLabel", { language: currentLang.name })}
            />
          </div>
        }
        output={
          <PlaygroundOutput
            result={result}
            runError={runError}
            isRunning={runMutation.isPending}
            onClear={clearOutput}
            fontSize={editorSettings.consoleFontSize}
            explain={{
              onClick: () => void handleExplainClick(),
              // `internal` unlocks Explain exactly as `pro` does (the backend
              // resolver treats them alike, `ide-plan-resolver.service.ts`), so
              // the padlock must not appear for an operator whose click works.
              locked:
                !isAuthenticated ||
                (idePlanData?.plan !== "pro" && idePlanData?.plan !== "internal"),
              disabled: runMutation.isPending || !sourceCode.trim() || !online,
              ...(!online ? { disabledReason: t("offlineExplainDisabled") } : {}),
            }}
            fixError={{
              onClick: () => void handleFixErrorClick(),
              eligible: fixErrorRequest !== null && !runMutation.isPending && online,
            }}
          />
        }
        stdin={
          <PlaygroundStdinPanel
            value={stdin}
            onChange={setStdin}
            fontSize={editorSettings.consoleFontSize}
          />
        }
        footer={
          omitFooter ? undefined : (
            <footer
              className="max-w-full break-words px-2 py-2.5 text-center text-[11px] leading-snug text-white/55 sm:px-3"
              role="contentinfo"
            >
              {t("footerLine", { language: currentLang.name })}
            </footer>
          )
        }
      />

      <SaveSnippetDialog
        open={saveDialogOpen}
        onOpenChange={setSaveDialogOpen}
        mode="create"
        initialTitle={snippetTitle}
        initialDescription={snippetDescription}
        isSubmitting={saveMutation.isPending || updateMutation.isPending}
        onSubmit={handleCreateSubmit}
      />

      <PlaygroundUpgradeModal
        open={explainUpgradeOpen}
        onClose={() => setExplainUpgradeOpen(false)}
        locale={locale}
      />

      <ExplainCodeModal
        open={explainModalOpen}
        onOpenChange={setExplainModalOpen}
        sourceCode={sourceCode}
        languageId={languageId}
      />

      <FixErrorModal
        open={fixModalOpen}
        onOpenChange={setFixModalOpen}
        request={fixErrorRequest}
        currentSource={sourceCode}
        onApplyFix={handleApplyFix}
      />
    </div>
  );
}

function PlaygroundFallback() {
  const t = useTranslations("playground");
  return (
    <div className="flex min-h-[40vh] flex-1 items-center justify-center text-sm text-white/50">
      {t("loading")}
    </div>
  );
}

export function PlaygroundClient({ renderedPerRequest = false, ...props }: PlaygroundClientProps) {
  if (renderedPerRequest) return <PlaygroundClientInner {...props} />;
  return (
    <Suspense fallback={<PlaygroundFallback />}>
      <PlaygroundClientInner {...props} />
    </Suspense>
  );
}
